
import React, { useEffect, useRef, useState } from "react";
/**
 * To improve the speed of typing
 * This component is like Monaco React but updating only on blur.
 * So typing is much faster
 *
 * @param props {{
 *  code:string
 * editorOptions:import("monaco-editor").editor.IEditorOptions
 * onChange:(value:string)=>void
 * setEditorObject?:(editor:import("monaco-editor").editor.ICodeEditor)=>void
 * highlightRange?
 * path?:string
 * }}
 */
function getLanguageForPath(path) {
  if (!path) return SOL_LANG_ID
  const lower = String(path).toLowerCase()
  if (lower.endsWith(".sol")) return SOL_LANG_ID
  if (lower.endsWith(".json")) return JSON_LANG_ID
  if (lower.endsWith(".js") || lower.endsWith(".jsx")) return "javascript"
  if (lower.endsWith(".ts") || lower.endsWith(".tsx")) return "typescript"
  if (lower.endsWith(".md")) return "markdown"
  if (lower.endsWith(".css")) return "css"
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "html"
  if (lower.endsWith(".yaml") || lower.endsWith(".yml")) return "yaml"
  return SOL_LANG_ID
}

// Use fresh language ids so we are not blocked by the prebuilt
// monaco-bundle's lazy tokenizer factories (which try to fetch a chunk
// that 404s on nested routes).
const SOL_LANG_ID = "solidity-inline"
const JSON_LANG_ID = "json-inline"

// Minimal Solidity Monarch tokenizer. Registered inline because the
// prebuilt monaco-bundle.js lazy-loads its own Solidity tokenizer chunk
// with an empty publicPath, which 404s on nested routes.
// NOTE: do not include identifiers that exist on Object.prototype
// (e.g. "constructor", "toString", "hasOwnProperty", "__proto__") here —
// monaco's Monarch compiler builds a lookup table via `obj[key] = true` on
// a plain object, which throws "Cannot assign to read only property
// 'constructor'". Handle those with dedicated rules in the tokenizer.
const SOLIDITY_KEYWORDS = [
  "pragma", "import", "from", "as", "contract", "library", "interface",
  "abstract", "is", "using", "for", "function", "modifier",
  "fallback", "receive", "event", "emit", "enum", "struct", "mapping",
  "return", "returns", "if", "else", "while", "do", "break",
  "continue", "throw", "try", "catch", "revert", "require", "assert",
  "new", "delete", "this", "super", "selfdestruct", "suicide", "memory",
  "storage", "calldata", "public", "private", "internal", "external",
  "pure", "view", "payable", "constant", "immutable", "virtual", "override",
  "indexed", "anonymous", "unchecked", "assembly", "let", "true", "false",
  "var",
]
const SOLIDITY_TYPES = [
  "address", "bool", "string", "bytes", "byte", "int", "uint", "fixed", "ufixed",
  ...Array.from({ length: 32 }, (_, i) => `bytes${i + 1}`),
  ...Array.from({ length: 32 }, (_, i) => `uint${(i + 1) * 8}`),
  ...Array.from({ length: 32 }, (_, i) => `int${(i + 1) * 8}`),
]
function deepCloneLanguage(obj) {
  if (obj === null || obj === undefined) return obj
  if (obj instanceof RegExp) return new RegExp(obj.source, obj.flags)
  if (Array.isArray(obj)) return obj.map(deepCloneLanguage)
  if (typeof obj === "object") {
    const out = {}
    for (const k of Object.keys(obj)) out[k] = deepCloneLanguage(obj[k])
    return out
  }
  return obj
}

const solidityMonarch = {
  defaultToken: "",
  tokenPostfix: ".sol",
  keywords: SOLIDITY_KEYWORDS,
  typeKeywords: SOLIDITY_TYPES,
  tokenizer: {
    root: [
      [/\/\/.*$/, "comment"],
      [/\/\*/, "comment", "@comment"],
      [/"([^"\\]|\\.)*"/, "string"],
      [/'([^'\\]|\\.)*'/, "string"],
      [/0[xX][0-9a-fA-F]+/, "number.hex"],
      [/\d+(\.\d+)?/, "number"],
      [/\b(constructor)\b/, "keyword"],
      [/[a-zA-Z_$][\w$]*/, {
        cases: {
          "@typeKeywords": "type",
          "@keywords": "keyword",
          "@default": "identifier",
        },
      }],
      [/[{}()\[\]]/, "@brackets"],
      [/[;,.]/, "delimiter"],
      [/[ \t\r\n]+/, ""],
    ],
    comment: [
      [/[^\/*]+/, "comment"],
      [/\*\//, "comment", "@pop"],
      [/[\/*]/, "comment"],
    ],
  },
}

let solidityRegistered = false
function ensureSolidityLanguage(monaco) {
  if (solidityRegistered || !monaco?.languages) return
  try {
    monaco.languages.register({
      id: SOL_LANG_ID,
      extensions: [".sol"],
      aliases: ["Solidity", "solidity"],
    })
  } catch (e) {
    // already registered, ignore
  }
  try {
    monaco.languages.setMonarchTokensProvider(SOL_LANG_ID, deepCloneLanguage(solidityMonarch))
    solidityRegistered = true
  } catch (e) {
    console.error("[fast-monaco] failed to register solidity tokens provider:", e)
  }
}

const CUSTOM_THEME_ID = "review-tool-light"
let customThemeDefined = false
function ensureCustomTheme(monaco) {
  if (customThemeDefined || !monaco?.editor?.defineTheme) return
  try {
    monaco.editor.defineTheme(CUSTOM_THEME_ID, {
      base: "vs",
      inherit: true,
      rules: [
        // Solidity tokens (tokenPostfix ".sol")
        { token: "keyword.sol", foreground: "0000FF", fontStyle: "bold" },
        { token: "type.sol", foreground: "267F99" },
        { token: "identifier.sol", foreground: "001080" },
        { token: "string.sol", foreground: "A31515" },
        { token: "string.escape.sol", foreground: "EE0000" },
        { token: "comment.sol", foreground: "008000", fontStyle: "italic" },
        { token: "number.sol", foreground: "098658" },
        { token: "number.hex.sol", foreground: "098658" },
        { token: "number.float.sol", foreground: "098658" },
        { token: "operator.sol", foreground: "000000" },
        { token: "delimiter.sol", foreground: "000000" },

        // JSON tokens (tokenPostfix ".json")
        { token: "keyword.json", foreground: "0000FF" },
        { token: "string.key.json", foreground: "A31515" },
        { token: "string.json", foreground: "0451A5" },
        { token: "number.json", foreground: "098658" },
        { token: "delimiter.json", foreground: "000000" },

        // Generic fallbacks
        { token: "keyword", foreground: "0000FF", fontStyle: "bold" },
        { token: "type", foreground: "267F99" },
        { token: "string", foreground: "A31515" },
        { token: "comment", foreground: "008000", fontStyle: "italic" },
        { token: "number", foreground: "098658" },
      ],
      colors: {},
    })
    customThemeDefined = true
  } catch (e) {
    // ignore
  }
}

const jsonMonarch = {
  defaultToken: "",
  tokenPostfix: ".json",
  keywords: ["true", "false", "null"],
  symbols: /[=><!~?:&|+\-*\/\^%]+/,
  escapes: /\\(?:[btnfr\\"\/]|u[0-9A-Fa-f]{4})/,
  tokenizer: {
    root: [
      [/[{}\[\]]/, "@brackets"],
      [/[,:]/, "delimiter"],
      [/"([^"\\]|\\.)*$/, "string.invalid"],
      [/"/, "string.key.json", "@string"],
      [/-?\d+\.\d+([eE][\-+]?\d+)?/, "number.float"],
      [/-?\d+/, "number"],
      [/(true|false|null)\b/, "keyword"],
      [/[ \t\r\n]+/, ""],
    ],
    string: [
      [/[^\\"]+/, "string"],
      [/@escapes/, "string.escape"],
      [/\\./, "string.escape.invalid"],
      [/"/, "string", "@pop"],
    ],
  },
}

let jsonRegistered = false
function ensureJsonLanguage(monaco) {
  if (jsonRegistered || !monaco?.languages) return
  try {
    monaco.languages.register({
      id: JSON_LANG_ID,
      extensions: [".json"],
      aliases: ["JSON", "json"],
    })
    monaco.languages.setMonarchTokensProvider(JSON_LANG_ID, jsonMonarch)
    jsonRegistered = true
  } catch (e) {
    // ignore
  }
}

function applyLanguage(monaco, editor, path) {
  if (!monaco || !editor) return
  ensureSolidityLanguage(monaco)
  ensureJsonLanguage(monaco)
  ensureCustomTheme(monaco)
  const model = editor.getModel?.()
  if (!model) return
  const language = getLanguageForPath(path)
  try {
    monaco.editor.setModelLanguage(model, language)
  } catch (e) {
    // language may not be registered; ignore
  }
  try {
    monaco.editor.setTheme(CUSTOM_THEME_ID)
  } catch (e) {
    // ignore
  }
}

function fastMonaco({ code, editorOptions, onChange, setEditorObject, highlightRange, path }) {
  /**
   * This type it just for autocomplete of `editorRef.current`
   * @type {import("monaco-editor").editor.ICodeEditor}
   * */
  let editorType
  var editorRef = useRef(editorType)
  var monacoRef = useRef()
  var onChangeRef = useRef(val => {})
  const [decorations, setDecorations] = useState([])
  const monacoObjects = useRef(null);

  onChangeRef.current = onChange // Update the ref to the latest onChange,
  // Since react-monaco not rebinding.
  /**
   * @param editor {import("monaco-editor").editor.ICodeEditor}
   * @param monaco {any}
   *  */
  function editorDidMount(editor, monaco) {
    
    monacoObjects.current = {
      editor,
      monaco,
  };
    setEditorObject?.(editor)
    editorRef.current = editor
    monacoRef.current = monaco
    editor.setValue(code)
    applyLanguage(monaco, editor, path)
    editor.onDidBlurEditorText(() => {
      onChangeRef.current(editor.getValue())
    })

  }
  useEffect(() => {
    editorRef.current?.setValue(code)
  }, [code])

  useEffect(() => {
    if (monacoObjects.current) {
      const { monaco, editor } = monacoObjects.current
      applyLanguage(monaco, editor, path)
    }
  }, [path])

  useEffect(() => {
    if (!monacoObjects.current || !highlightRange) {
        return;
    } else {
        const { monaco, editor } = monacoObjects.current;
        if(
            !highlightRange || highlightRange.length === 0 || 
            (highlightRange?.startLine == 1 &&
            highlightRange?.startColumn == 1 &&
            highlightRange?.endLine == 1 &&
            highlightRange?.endColumn == 1)
        ) {
            editor.deltaDecorations(decorations, []);
        } else {
            let deco = editor.deltaDecorations(
                decorations,
                [
                  {
                    range: new monaco.Range(
                      highlightRange?.startLine,
                      highlightRange?.startColumn,
                      highlightRange?.endLine,
                      highlightRange?.endColumn
                  ),
                    options: {
                      language: "solidity",
                      isWholeLine: false,
                      className: "myContentClass",
                      glyphMarginClassName: "myGlyphMarginClass",
                    }
                  },
                ]
              );
              setDecorations(deco);
        }

    } 
}, [highlightRange]);
  return (
    <>
    <style
    dangerouslySetInnerHTML={{
__html: `
        .myGlyphMarginClass {
            background: red;
        }
        .myContentClass {
            background: lightblue;
        }
        `,
    }}
/>
    <MonacoReact
      editorDidMount={editorDidMount}
      value={code}
      options={editorOptions}
    />
    </>
  )
}

export default fastMonaco
